import { useState } from "react";
import { useEntry, Loading, ErrorBox, Chip, STATE_META, IntegrityNote } from "./entryShared";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

const Field = ({ label, value }) => value ? (
  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">{label}</p><p className="text-[11px] text-slate-700">{value}</p></div>
) : null;

export const CaseEvidence = ({ caseId }) => {
  const { data, error } = useEntry(caseId, "/evidence");
  const [open, setOpen] = useState(null);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <div data-testid="case-evidence" className="mt-4">
      <div className="flex flex-wrap gap-4 mb-4">
        {Object.entries(data.state_counts || {}).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2" data-testid={`state-count-${k}`}>
            <Chip meta={STATE_META[k]} /><span className="font-heading font-bold text-slate-900">{v}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {(data.assets || []).map((a) => (
          <div key={a.id} className="border border-slate-200 bg-white" data-testid={`evidence-${a.id}`}>
            <button onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`evidence-toggle-${a.id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{a.name}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">{a.type?.replaceAll("_", " ")} · {a.country} · {a.year} · {a.source}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Chip meta={STATE_META[a.evidence_state]} />
                {open === a.id ? <CaretUp size={12} /> : <CaretDown size={12} />}
              </div>
            </button>
            {open === a.id && (
              <div className="border-t border-slate-100 px-4 py-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`evidence-detail-${a.id}`}>
                <Field label="Population" value={a.population} />
                <Field label="Intervention" value={a.intervention} />
                <Field label="Comparator" value={a.comparator} />
                <Field label="Outcome" value={a.outcome} />
                <Field label="Perspective" value={a.perspective} />
                <Field label="Setting" value={a.setting} />
                <Field label="Decision body" value={a.decision_body} />
                <Field label="Healthcare system" value={a.healthcare_system?.replaceAll("_", " ")} />
                <Field label="Economic inputs" value={a.economic_inputs} />
                <Field label="Clinical inputs" value={a.clinical_inputs} />
                <Field label="Resource-use inputs" value={a.resource_inputs} />
                <Field label="Limitations" value={a.limitations} />
                <Field label="Source confidence" value={`${a.source_confidence || "—"}${a.source_verified ? " · verified" : " · verification pending"}`} />
                {a.provenance && (
                  <div className="sm:col-span-2 lg:col-span-3 bg-slate-50 px-3 py-2 border-l-2 border-emerald-600" data-testid={`provenance-${a.id}`}>
                    <p className="text-[9px] uppercase tracking-widest text-slate-400">Provenance</p>
                    <p className="text-[11px] text-slate-700">{a.provenance.document} · {a.provenance.location} · {a.provenance.date}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <IntegrityNote text={data.provenance_note} />
    </div>
  );
};
